import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { 
    Shield, 
    ShieldAlert, 
    ShieldCheck, 
    ShieldX,
    Ban,
    Unlock,
    Activity,
    AlertTriangle,
    AlertCircle,
    CheckCircle,
    Clock,
    Globe,
    Server,
    RefreshCw,
    ChevronLeft,
    Eye,
    Filter,
    Search
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '../components/ui/dialog';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Severity badge colors
const severityColors = {
    CRITICAL: 'bg-red-600 text-white',
    HIGH: 'bg-orange-500 text-white',
    MEDIUM: 'bg-yellow-500 text-black',
    LOW: 'bg-blue-500 text-white'
};

// Status badge colors
const statusColors = {
    NEW: 'bg-red-100 text-red-800 border-red-200',
    INVESTIGATING: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    RESOLVED: 'bg-green-100 text-green-800 border-green-200',
    FALSE_POSITIVE: 'bg-gray-100 text-gray-800 border-gray-200'
};

// Incident type icons
const incidentTypeIcons = {
    IP_BLOCKED: Ban,
    BRUTE_FORCE: ShieldAlert,
    PATH_ENUMERATION: Search,
    RAPID_REQUESTS: Activity,
    INVALID_SIGNATURE: ShieldX,
    MANUAL_IP_BLOCK: Ban,
    default: AlertTriangle
};

export default function SecurityDashboardPage() {
    const navigate = useNavigate();
    const { user, isNetworkManager, loading: authLoading } = useAuth();
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    
    // Data states
    const [stats, setStats] = useState(null);
    const [blockedIPs, setBlockedIPs] = useState({});
    const [incidents, setIncidents] = useState([]);
    
    // Filter states
    const [severityFilter, setSeverityFilter] = useState('all');
    const [statusFilter, setStatusFilter] = useState('all');
    const [searchQuery, setSearchQuery] = useState('');
    
    // Dialog states
    const [blockIPDialog, setBlockIPDialog] = useState(false);
    const [blockIPForm, setBlockIPForm] = useState({ ip: '', reason: '', duration: 24 });
    const [resolveDialog, setResolveDialog] = useState(null);
    const [incidentDetailDialog, setIncidentDetailDialog] = useState(null);

    // Check access after auth is loaded
    useEffect(() => {
        if (!authLoading && user && !isNetworkManager()) {
            navigate('/portal');
            toast.error('Access denied. Network Manager or Administrator required.');
        }
    }, [user, authLoading, navigate]); // isNetworkManager is stable since it only depends on user

    const fetchData = useCallback(async () => {
        try {
            const [statsRes, blockedRes, incidentsRes] = await Promise.all([
                axios.get(`${API_URL}/admin/security/stats`),
                axios.get(`${API_URL}/admin/security/blocked-ips`),
                axios.get(`${API_URL}/admin/security/incidents?limit=100`)
            ]);
            
            setStats(statsRes.data);
            setBlockedIPs(blockedRes.data.blocked_ips || {});
            setIncidents(incidentsRes.data.incidents || []);
        } catch (error) {
            console.error('Failed to fetch security data:', error);
            // Only show error if it's not a 401 (auth issue will be handled by interceptor)
            if (error.response?.status !== 401) {
                toast.error('Failed to load security data');
            }
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, []); // No dependencies needed - uses only setters and API_URL which are stable

    useEffect(() => {
        // Wait for auth to be fully loaded before fetching
        if (user && !authLoading) {
            // Small delay to ensure token is set in axios interceptor
            const timer = setTimeout(() => {
                fetchData();
            }, 100);
            return () => clearTimeout(timer);
        }
    }, [fetchData, user, authLoading]);
    
    // Separate effect for auto-refresh interval
    useEffect(() => {
        if (!user || authLoading) return;
        
        // Refresh every 30 seconds
        const interval = setInterval(() => {
            fetchData();
        }, 30000);
        
        return () => clearInterval(interval);
    }, [fetchData, user, authLoading]);

    const handleRefresh = () => {
        setRefreshing(true);
        fetchData();
    };

    const handleBlockIP = async () => {
        if (!blockIPForm.ip || !blockIPForm.reason) {
            toast.error('Please fill in all required fields');
            return;
        }
        
        try {
            await axios.post(`${API_URL}/admin/security/block-ip`, null, {
                params: {
                    ip: blockIPForm.ip,
                    reason: blockIPForm.reason,
                    duration_hours: blockIPForm.duration
                }
            });
            toast.success(`IP ${blockIPForm.ip} blocked successfully`);
            setBlockIPDialog(false);
            setBlockIPForm({ ip: '', reason: '', duration: 24 });
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to block IP');
        }
    };

    const handleUnblockIP = async (ip) => {
        try {
            await axios.post(`${API_URL}/admin/security/unblock-ip`, null, {
                params: { ip }
            });
            toast.success(`IP ${ip} unblocked successfully`);
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to unblock IP');
        }
    };

    const handleResolveIncident = async (incidentId, resolution) => {
        try {
            await axios.post(`${API_URL}/admin/security/incidents/${incidentId}/resolve`, null, {
                params: { resolution }
            });
            toast.success(`Incident marked as ${resolution}`);
            setResolveDialog(null);
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to resolve incident');
        }
    };

    // Filter incidents
    const filteredIncidents = incidents.filter(incident => {
        if (severityFilter !== 'all' && incident.severity !== severityFilter) return false;
        if (statusFilter !== 'all' && incident.status !== statusFilter) return false;
        if (searchQuery) {
            const query = searchQuery.toLowerCase();
            return (
                incident.ip_address?.toLowerCase().includes(query) ||
                incident.type?.toLowerCase().includes(query) ||
                incident.details?.reason?.toLowerCase().includes(query)
            );
        }
        return true;
    });

    const formatTimestamp = (timestamp) => {
        if (!timestamp) return 'N/A';
        return new Date(timestamp).toLocaleString();
    };

    const formatDuration = (seconds) => {
        if (!seconds) return 'N/A';
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        if (hours > 0) return `${hours}h ${minutes}m`;
        return `${minutes}m`;
    };

    if (authLoading || loading) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center">
                <div className="text-center">
                    <Shield className="w-12 h-12 text-primary animate-pulse mx-auto mb-4" />
                    <p className="text-muted-foreground">Loading security data...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background" data-testid="security-dashboard">
            {/* Header */}
            <header className="bg-card border-b border-border sticky top-0 z-30">
                <div className="flex items-center justify-between h-14 px-4">
                    <div className="flex items-center gap-3">
                        <Button 
                            variant="ghost" 
                            size="icon" 
                            onClick={() => navigate('/dashboard')}
                            data-testid="back-btn"
                        >
                            <ChevronLeft className="h-5 w-5" />
                        </Button>
                        <div className="flex items-center gap-2">
                            <Shield className="h-5 w-5 text-primary" />
                            <h1 className="text-lg font-semibold">Security Dashboard</h1>
                        </div>
                    </div>
                    <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={handleRefresh}
                        disabled={refreshing}
                        data-testid="refresh-btn"
                    >
                        <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                        Refresh
                    </Button>
                </div>
            </header>

            <div className="p-4 space-y-6 max-w-7xl mx-auto">
                {/* Stats Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <Card data-testid="stat-total-incidents">
                        <CardContent className="p-4">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-muted-foreground">Total Incidents</p>
                                    <p className="text-2xl font-bold">{stats?.total_incidents || 0}</p>
                                </div>
                                <AlertTriangle className="h-8 w-8 text-orange-500 opacity-50" />
                            </div>
                        </CardContent>
                    </Card>
                    
                    <Card data-testid="stat-24h-incidents">
                        <CardContent className="p-4">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-muted-foreground">Last 24 Hours</p>
                                    <p className="text-2xl font-bold">{stats?.incidents_last_24h || 0}</p>
                                </div>
                                <Clock className="h-8 w-8 text-blue-500 opacity-50" />
                            </div>
                        </CardContent>
                    </Card>
                    
                    <Card data-testid="stat-blocked-ips">
                        <CardContent className="p-4">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-muted-foreground">Blocked IPs</p>
                                    <p className="text-2xl font-bold">{stats?.blocked_ips_count || 0}</p>
                                </div>
                                <Ban className="h-8 w-8 text-red-500 opacity-50" />
                            </div>
                        </CardContent>
                    </Card>
                    
                    <Card data-testid="stat-new-incidents">
                        <CardContent className="p-4">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-muted-foreground">New/Unresolved</p>
                                    <p className="text-2xl font-bold text-red-600">
                                        {(stats?.by_status?.NEW || 0) + (stats?.by_status?.INVESTIGATING || 0)}
                                    </p>
                                </div>
                                <ShieldAlert className="h-8 w-8 text-red-500 opacity-50" />
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* Severity Breakdown */}
                {stats?.by_severity && Object.keys(stats.by_severity).length > 0 && (
                    <Card>
                        <CardHeader className="pb-2">
                            <CardTitle className="text-base">Incidents by Severity</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="flex flex-wrap gap-3">
                                {['CRITICAL', 'HIGH', 'MEDIUM', 'LOW'].map(severity => (
                                    <div key={severity} className="flex items-center gap-2">
                                        <Badge className={severityColors[severity]}>
                                            {severity}
                                        </Badge>
                                        <span className="font-semibold">{stats.by_severity[severity] || 0}</span>
                                    </div>
                                ))}
                            </div>
                        </CardContent>
                    </Card>
                )}

                {/* Blocked IPs Section */}
                <Card>
                    <CardHeader className="flex flex-row items-center justify-between pb-2">
                        <div>
                            <CardTitle className="text-base flex items-center gap-2">
                                <Ban className="h-4 w-4" />
                                Blocked IP Addresses
                            </CardTitle>
                            <CardDescription>
                                {Object.keys(blockedIPs).length} IP(s) currently blocked
                            </CardDescription>
                        </div>
                        <Button 
                            size="sm" 
                            onClick={() => setBlockIPDialog(true)}
                            data-testid="block-ip-btn"
                        >
                            <Ban className="h-4 w-4 mr-2" />
                            Block IP
                        </Button>
                    </CardHeader>
                    <CardContent>
                        {Object.keys(blockedIPs).length === 0 ? (
                            <div className="text-center py-8 text-muted-foreground">
                                <ShieldCheck className="h-12 w-12 mx-auto mb-2 opacity-50" />
                                <p>No IP addresses are currently blocked</p>
                            </div>
                        ) : (
                            <div className="space-y-2">
                                {Object.entries(blockedIPs).map(([ip, info]) => (
                                    <div 
                                        key={ip} 
                                        className="flex items-center justify-between p-3 bg-red-50 dark:bg-red-950/30 rounded-lg border border-red-200 dark:border-red-800"
                                        data-testid={`blocked-ip-${ip.replace(/\./g, '-')}`}
                                    >
                                        <div className="flex items-center gap-3">
                                            <Globe className="h-5 w-5 text-red-500" />
                                            <div>
                                                <p className="font-mono font-medium">{ip}</p>
                                                <p className="text-xs text-muted-foreground">
                                                    Expires: {formatTimestamp(info.blocked_until)} 
                                                    ({formatDuration(info.remaining_seconds)} remaining)
                                                </p>
                                            </div>
                                        </div>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => handleUnblockIP(ip)}
                                            data-testid={`unblock-${ip.replace(/\./g, '-')}`}
                                        >
                                            <Unlock className="h-4 w-4 mr-1" />
                                            Unblock
                                        </Button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* Security Incidents Section */}
                <Card>
                    <CardHeader className="pb-2">
                        <CardTitle className="text-base flex items-center gap-2">
                            <AlertCircle className="h-4 w-4" />
                            Security Incidents
                        </CardTitle>
                        <CardDescription>
                            {filteredIncidents.length} incident(s) found
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        {/* Filters */}
                        <div className="flex flex-wrap gap-3 mb-4">
                            <div className="flex-1 min-w-[200px]">
                                <Input
                                    placeholder="Search by IP or type..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="h-9"
                                    data-testid="incident-search"
                                />
                            </div>
                            <Select value={severityFilter} onValueChange={setSeverityFilter}>
                                <SelectTrigger className="w-[140px] h-9" data-testid="severity-filter">
                                    <SelectValue placeholder="Severity" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">All Severities</SelectItem>
                                    <SelectItem value="CRITICAL">Critical</SelectItem>
                                    <SelectItem value="HIGH">High</SelectItem>
                                    <SelectItem value="MEDIUM">Medium</SelectItem>
                                    <SelectItem value="LOW">Low</SelectItem>
                                </SelectContent>
                            </Select>
                            <Select value={statusFilter} onValueChange={setStatusFilter}>
                                <SelectTrigger className="w-[140px] h-9" data-testid="status-filter">
                                    <SelectValue placeholder="Status" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">All Statuses</SelectItem>
                                    <SelectItem value="NEW">New</SelectItem>
                                    <SelectItem value="INVESTIGATING">Investigating</SelectItem>
                                    <SelectItem value="RESOLVED">Resolved</SelectItem>
                                    <SelectItem value="FALSE_POSITIVE">False Positive</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>

                        {/* Incidents Table */}
                        {filteredIncidents.length === 0 ? (
                            <div className="text-center py-8 text-muted-foreground">
                                <ShieldCheck className="h-12 w-12 mx-auto mb-2 opacity-50" />
                                <p>No security incidents found</p>
                            </div>
                        ) : (
                            <div className="border rounded-lg overflow-hidden">
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Type</TableHead>
                                            <TableHead>Severity</TableHead>
                                            <TableHead>IP Address</TableHead>
                                            <TableHead>Status</TableHead>
                                            <TableHead>Time</TableHead>
                                            <TableHead className="text-right">Actions</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {filteredIncidents.slice(0, 50).map((incident) => {
                                            const IconComponent = incidentTypeIcons[incident.type] || incidentTypeIcons.default;
                                            return (
                                                <TableRow key={incident.id} data-testid={`incident-${incident.id}`}>
                                                    <TableCell>
                                                        <div className="flex items-center gap-2">
                                                            <IconComponent className="h-4 w-4 text-muted-foreground" />
                                                            <span className="text-sm font-medium">
                                                                {incident.type?.replace(/_/g, ' ')}
                                                            </span>
                                                        </div>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Badge className={severityColors[incident.severity] || 'bg-gray-500'}>
                                                            {incident.severity}
                                                        </Badge>
                                                    </TableCell>
                                                    <TableCell>
                                                        <span className="font-mono text-sm">{incident.ip_address}</span>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Badge variant="outline" className={statusColors[incident.status]}>
                                                            {incident.status}
                                                        </Badge>
                                                    </TableCell>
                                                    <TableCell className="text-sm text-muted-foreground">
                                                        {formatTimestamp(incident.timestamp)}
                                                    </TableCell>
                                                    <TableCell className="text-right">
                                                        <div className="flex justify-end gap-1">
                                                            <Button
                                                                variant="ghost"
                                                                size="sm"
                                                                onClick={() => setIncidentDetailDialog(incident)}
                                                                data-testid={`view-incident-${incident.id}`}
                                                            >
                                                                <Eye className="h-4 w-4" />
                                                            </Button>
                                                            {incident.status === 'NEW' && (
                                                                <Button
                                                                    variant="ghost"
                                                                    size="sm"
                                                                    onClick={() => setResolveDialog(incident)}
                                                                    data-testid={`resolve-incident-${incident.id}`}
                                                                >
                                                                    <CheckCircle className="h-4 w-4 text-green-600" />
                                                                </Button>
                                                            )}
                                                        </div>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* Security Notice */}
                <Card className="bg-blue-50 dark:bg-blue-950/30 border-blue-200 dark:border-blue-800">
                    <CardContent className="p-4">
                        <div className="flex items-start gap-3">
                            <Server className="h-5 w-5 text-blue-600 mt-0.5" />
                            <div className="text-sm">
                                <p className="font-medium text-blue-800 dark:text-blue-200">Security Monitoring Active</p>
                                <p className="text-blue-600 dark:text-blue-400 mt-1">
                                    The system automatically detects and blocks suspicious activity including brute force attacks, 
                                    path enumeration, and rapid request floods. IPs are auto-blocked after 10 failed login attempts.
                                </p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Block IP Dialog */}
            <Dialog open={blockIPDialog} onOpenChange={setBlockIPDialog}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Block IP Address</DialogTitle>
                        <DialogDescription>
                            Manually block an IP address from accessing the system.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div>
                            <Label htmlFor="block-ip">IP Address *</Label>
                            <Input
                                id="block-ip"
                                placeholder="e.g., 192.168.1.100"
                                value={blockIPForm.ip}
                                onChange={(e) => setBlockIPForm({...blockIPForm, ip: e.target.value})}
                                data-testid="block-ip-input"
                            />
                        </div>
                        <div>
                            <Label htmlFor="block-reason">Reason *</Label>
                            <Input
                                id="block-reason"
                                placeholder="e.g., Suspicious activity detected"
                                value={blockIPForm.reason}
                                onChange={(e) => setBlockIPForm({...blockIPForm, reason: e.target.value})}
                                data-testid="block-reason-input"
                            />
                        </div>
                        <div>
                            <Label htmlFor="block-duration">Duration (hours)</Label>
                            <Select 
                                value={String(blockIPForm.duration)} 
                                onValueChange={(v) => setBlockIPForm({...blockIPForm, duration: parseInt(v)})}
                            >
                                <SelectTrigger data-testid="block-duration-select">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="1">1 hour</SelectItem>
                                    <SelectItem value="6">6 hours</SelectItem>
                                    <SelectItem value="12">12 hours</SelectItem>
                                    <SelectItem value="24">24 hours</SelectItem>
                                    <SelectItem value="48">48 hours</SelectItem>
                                    <SelectItem value="168">1 week</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setBlockIPDialog(false)}>Cancel</Button>
                        <Button onClick={handleBlockIP} data-testid="confirm-block-ip">Block IP</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Resolve Incident Dialog */}
            <Dialog open={!!resolveDialog} onOpenChange={() => setResolveDialog(null)}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Resolve Incident</DialogTitle>
                        <DialogDescription>
                            How would you like to resolve this security incident?
                        </DialogDescription>
                    </DialogHeader>
                    {resolveDialog && (
                        <div className="py-4">
                            <div className="p-3 bg-muted rounded-lg mb-4">
                                <p className="font-medium">{resolveDialog.type?.replace(/_/g, ' ')}</p>
                                <p className="text-sm text-muted-foreground">IP: {resolveDialog.ip_address}</p>
                                <p className="text-sm text-muted-foreground">{formatTimestamp(resolveDialog.timestamp)}</p>
                            </div>
                        </div>
                    )}
                    <DialogFooter className="flex-col sm:flex-row gap-2">
                        <Button 
                            variant="outline" 
                            onClick={() => setResolveDialog(null)}
                            className="w-full sm:w-auto"
                        >
                            Cancel
                        </Button>
                        <Button 
                            variant="secondary"
                            onClick={() => handleResolveIncident(resolveDialog?.id, 'FALSE_POSITIVE')}
                            className="w-full sm:w-auto"
                            data-testid="resolve-false-positive"
                        >
                            False Positive
                        </Button>
                        <Button 
                            onClick={() => handleResolveIncident(resolveDialog?.id, 'RESOLVED')}
                            className="w-full sm:w-auto"
                            data-testid="resolve-confirmed"
                        >
                            <CheckCircle className="h-4 w-4 mr-2" />
                            Mark Resolved
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Incident Detail Dialog */}
            <Dialog open={!!incidentDetailDialog} onOpenChange={() => setIncidentDetailDialog(null)}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle>Incident Details</DialogTitle>
                    </DialogHeader>
                    {incidentDetailDialog && (
                        <div className="space-y-4 py-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <Label className="text-xs text-muted-foreground">Type</Label>
                                    <p className="font-medium">{incidentDetailDialog.type?.replace(/_/g, ' ')}</p>
                                </div>
                                <div>
                                    <Label className="text-xs text-muted-foreground">Severity</Label>
                                    <Badge className={severityColors[incidentDetailDialog.severity]}>
                                        {incidentDetailDialog.severity}
                                    </Badge>
                                </div>
                                <div>
                                    <Label className="text-xs text-muted-foreground">IP Address</Label>
                                    <p className="font-mono">{incidentDetailDialog.ip_address}</p>
                                </div>
                                <div>
                                    <Label className="text-xs text-muted-foreground">Status</Label>
                                    <Badge variant="outline" className={statusColors[incidentDetailDialog.status]}>
                                        {incidentDetailDialog.status}
                                    </Badge>
                                </div>
                                <div>
                                    <Label className="text-xs text-muted-foreground">Timestamp</Label>
                                    <p className="text-sm">{formatTimestamp(incidentDetailDialog.timestamp)}</p>
                                </div>
                                {incidentDetailDialog.user_id && (
                                    <div>
                                        <Label className="text-xs text-muted-foreground">User ID</Label>
                                        <p className="text-sm font-mono">{incidentDetailDialog.user_id}</p>
                                    </div>
                                )}
                            </div>
                            {incidentDetailDialog.details && Object.keys(incidentDetailDialog.details).length > 0 && (
                                <div>
                                    <Label className="text-xs text-muted-foreground">Details</Label>
                                    <pre className="mt-1 p-3 bg-muted rounded-lg text-xs overflow-auto max-h-40">
                                        {JSON.stringify(incidentDetailDialog.details, null, 2)}
                                    </pre>
                                </div>
                            )}
                            {incidentDetailDialog.resolved_at && (
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <Label className="text-xs text-muted-foreground">Resolved At</Label>
                                        <p className="text-sm">{formatTimestamp(incidentDetailDialog.resolved_at)}</p>
                                    </div>
                                    <div>
                                        <Label className="text-xs text-muted-foreground">Resolved By</Label>
                                        <p className="text-sm">{incidentDetailDialog.resolved_by}</p>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setIncidentDetailDialog(null)}>Close</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
